/* Laserlab booking panel
   The booking app (Servicify, React) owns availability, the booking request and its
   markup. This layer presents its dialogs as the site's right-hand panel (styles in
   laserlab-booking.css) and keeps the flow calm and Norwegian:
   - a header ("Velg tid" + the shared close control) and a summary of the treatment,
   - 24-hour start times, Norwegian dates and copy,
   - the first available date selected when the calendar opens or changes month,
   - drawer behaviour: Escape and outside click close the whole booking, focus moves to
     the close control and back to the booking button, and the page does not scroll.
   Provider nodes are never moved or removed. Text is changed in place (nodeValue) and
   attributes are only added, so React keeps working. If the provider changes its markup
   the selectors stop matching and its own interface shows through. */
(() => {
  'use strict';

  if (window.LaserlabBooking) return;

  const PORTAL = '.servicify-modal-portal';
  const CONTENT = '.ReactModal__Content';
  const CLOSE = '.servicify-modal-close';
  const TRIGGER = '.servicify-select-a-time-button, .servicify-popup-calendar-button';
  const UI = 'data-ll-booking-ui';
  const TIME_FORMAT_KEY = 'servicify-time-format';
  const OBSERVE = { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['aria-label', 'class', 'aria-pressed'] };
  const OBSERVE_IDLE = { childList: true };

  const WEEKDAYS = ['søndag', 'mandag', 'tirsdag', 'onsdag', 'torsdag', 'fredag', 'lørdag'];
  const WEEKDAYS_EN = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const WEEKDAY_ABBR = { mo: 'ma', tu: 'ti', we: 'on', th: 'to', fr: 'fr', sa: 'lø', su: 'sø' };
  const MONTHS = ['januar', 'februar', 'mars', 'april', 'mai', 'juni', 'juli', 'august', 'september', 'oktober', 'november', 'desember'];
  const MONTHS_EN = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const MONTH_INDEX = { jan: 0, feb: 1, mar: 2, apr: 3, may: 4, mai: 4, jun: 5, jul: 6, aug: 7, sep: 8, oct: 9, okt: 9, nov: 10, dec: 11, des: 11 };

  // Exact provider strings (English defaults and merchant texts) shown in the booking flow.
  const COPY = new Map([
    ['Select a date to view available times.', 'Velg en dato for å se ledige tider.'],
    ['Please select another date', 'Velg en annen dato.'],
    ['Ingen timer tilgjengelig', 'Ingen ledige timer denne dagen'],
    ['No timeslots available.', 'Ingen ledige timer denne dagen'],
    ['No timeslots available for this date', 'Ingen ledige timer denne dagen'],
    ['No upcoming dates available.', 'Ingen ledige datoer fremover.'],
    ['Next available date', 'Neste ledige dato'],
    ['Show dates in a list', 'Vis datoene som liste'],
    ['Please select a time', 'Velg et tidspunkt'],
    ['Select a time', 'Velg tid'],
    ['Sold out', 'Fullt'],
    ['Selected', 'Valgt'],
    ['Book with', 'Behandler'],
    ['Any available', 'Første ledige'],
    ['Email', 'E-post'],
    ['This field is required.', 'Må fylles ut.'],
    ['Please enter a valid email address.', 'Skriv inn en gyldig e-postadresse.'],
    ['Please fill in the required fields to continue.', 'Fyll ut feltene for å fortsette.'],
    ['Confirming...', 'Bekrefter …'],
    ['Confirming your booking…', 'Bekrefter bestillingen …'],
    ['Your booking could not be saved at this time.', 'Bestillingen kunne ikke lagres akkurat nå.'],
    ['Please refresh the page and try again.', 'Last inn siden på nytt og prøv igjen.'],
    ['This timeslot is unavailable.', 'Denne tiden er ikke tilgjengelig.'],
    ['You can only book this event once per day.', 'Du kan bare bestille denne behandlingen én gang per dag.'],
    ['Adjust quantity', 'Endre antall'],
    ['Close', 'Lukk'],
    ['Optional', 'Valgfritt'],
    ['Type to search countries', 'Skriv for å søke etter land'],
  ]);
  const ARIA = new Map([
    ['Next Month', 'Neste måned'],
    ['Previous Month', 'Forrige måned'],
    ['Close', 'Lukk'],
    ['Select country', 'Velg landskode'],
    ['Time format', 'Tidsformat'],
    ['Country', 'Land'],
  ]);
  const ICON_FALLBACK = {
    close: '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" class="icon icon-close icon--large" fill="none" viewBox="0 0 24 24"><path d="M18.75 5.13496L5.25 18.8544" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/><path d="M18.75 18.8544L5.25 5.13496" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    back: '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" class="icon icon-arrow-left" fill="none" viewBox="0 0 24 24"><path d="M19.25 12H4.75m6-6-6 6 6 6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>',
  };
  const TIME_RANGE = /^\s*(\d{1,2})[:.](\d{2})\s*([AaPp]\.?[Mm]\.?)?\s*(?:[-–]\s*(\d{1,2})[:.](\d{2})\s*([AaPp]\.?[Mm]\.?)?)?\s*$/;
  const DATE_IN_TEXT = /(\d{1,2})\.?\s+([A-Za-zÆØÅæøå]{3,})\.?,?\s+(\d{4})/;
  const DATE_IN_TEXT_US = /([A-Za-z]{3,})\.?\s+(\d{1,2})(?:st|nd|rd|th)?,?\s+(\d{4})/;
  const HOME_TIMEZONE = 'Europe/Oslo';

  const state = {
    newsletter: null,
    subscribed: '',
    codePending: '',
    trigger: null,
    base: null,
    context: null,
    openedAt: 0,
    initial: false,
    hidden: false,
    monthSkips: 0,
    emptyDaySkips: 0,
    picks: {},
    autoTimer: 0,
    settleTimer: 0,
    fallbackTimer: 0,
    focused: new WeakSet(),
    seq: 0,
    requestedAt: 0,
  };

  /* Booking app health, from resource timing: once its event lookup has answered and its calendar
     code has loaded, the app draws its calendar within a moment. If it still shows nothing of its own
     a second after the button was pressed, it has given up (for example an event that no longer
     exists). The fallback then shows at once instead of after the 8 s backstop. */
  const appLoad = { lookup: 0, view: 0 };

  /* Helpers */
  const clean = (value) => (value || '').replace(/\s+/g, ' ').trim();
  const cap = (value) => value.charAt(0).toUpperCase() + value.slice(1);
  const setText = (node, value) => { if (node.nodeValue !== value) node.nodeValue = value; };
  const setAttr = (el, name, value) => { if (el.getAttribute(name) !== value) el.setAttribute(name, value); };
  const isEl = (node) => node instanceof Element;
  const DISMISSED = 'data-ll-dismissed';
  const openContents = () => Array.from(document.querySelectorAll(`${PORTAL}:not([${DISMISSED}]) ${CONTENT}`)).filter((el) => el.isConnected);
  const isDetails = (content) => content.classList.contains('Servicify-Modal') || !!content.querySelector('.Servicify-NoCheckout-Modal, [data-servicify="booking-preview-title"]');
  const directClose = (content) => Array.from(content.children).find((el) => el.matches(CLOSE)) || null;
  // The calendar dialog is the base layer; the details step opens as a second dialog on top of it.
  const baseOf = (contents) => contents.find((el) => !isDetails(el)) || contents[0] || null;
  const firstText = (el) => (el ? Array.from(el.childNodes).find((n) => n.nodeType === Node.TEXT_NODE && n.nodeValue.trim()) : null);

  function prefer24h() {
    try { window.localStorage.setItem(TIME_FORMAT_KEY, '24h'); } catch (error) { /* storage unavailable: the toggle fallback below applies */ }
  }

  function parseDate(text) {
    let day;
    let monthName;
    let year;
    let match = DATE_IN_TEXT.exec(text || '');
    if (match) [, day, monthName, year] = match;
    else if ((match = DATE_IN_TEXT_US.exec(text || ''))) [, monthName, day, year] = match;
    else return null;
    const month = MONTH_INDEX[monthName.slice(0, 3).toLowerCase()];
    if (month === undefined) return null;
    const date = new Date(Number(year), month, Number(day));
    return date.getMonth() === month ? date : null;
  }

  // "Tidssone: America/New_York" -> "America/New_York"
  const timezoneIn = (text) => {
    const match = /([A-Z][A-Za-z_]+\/[A-Za-z_\/]+)/.exec(text || '');
    return match ? match[1] : '';
  };

  const longDate = (date) => `${cap(WEEKDAYS[date.getDay()])} ${date.getDate()}. ${MONTHS[date.getMonth()]}`;
  const isoDate = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

  function to24(hours, minutes, meridiem) {
    let h = Number(hours);
    const m = (meridiem || '').replace(/\./g, '').toLowerCase();
    if (m === 'pm' && h < 12) h += 12;
    if (m === 'am' && h === 12) h = 0;
    return `${String(h).padStart(2, '0')}:${minutes}`;
  }

  function parseTime(text) {
    const match = /(\d{1,2})[:.](\d{2})\s*([AaPp]\.?[Mm]\.?)?/.exec(text || '');
    return match ? to24(match[1], match[2], match[3]) : '';
  }

  function icon(name) {
    const template = document.getElementById('LaserlabBookingIcons');
    const source = template && template.content.querySelector(`[data-icon="${name}"] svg`);
    let svg = source ? source.cloneNode(true) : null;
    if (!svg) {
      const holder = document.createElement('span');
      holder.innerHTML = ICON_FALLBACK[name];
      svg = holder.firstElementChild;
    }
    svg.setAttribute(UI, 'icon');
    return svg;
  }

  /* Treatment summary: theme product data, the heading next to the booking button, then the app. */
  function readData() {
    const script = document.getElementById('LaserlabBookingData');
    if (!script) return null;
    try { return JSON.parse(script.textContent); } catch (error) { return null; }
  }

  const shortDuration = (value) => clean(value).replace(/\bminutt(?:er)?\b/gi, 'min');

  function resolveContext(content) {
    if (state.context) return state.context;
    const trigger = state.trigger;
    const products = (readData() || {}).products || {};
    let productId = null;
    let variantId = null;
    if (trigger) {
      const href = trigger.getAttribute('data-original-href') || trigger.getAttribute('data-href') || '';
      const query = href.indexOf('?') > -1 ? new URLSearchParams(href.slice(href.indexOf('?') + 1)) : null;
      if (query) {
        productId = query.get('servicify-product');
        variantId = query.get('servicify-variant');
      }
      if (!variantId) {
        const form = trigger.form || trigger.closest('form') || (trigger.closest('product-form, .product-form') || document.createElement('i')).querySelector('form');
        const input = form && form.querySelector('[name="id"]');
        variantId = input ? input.value : null;
      }
    }
    const ids = Object.keys(products);
    if (!productId && ids.length === 1) productId = ids[0];
    const product = productId ? products[productId] : null;
    const variants = (product && product.variants) || {};
    const variant = variants[variantId] || variants[Object.keys(variants)[0]] || null;

    let title = '';
    if (product) {
      const heading = document.querySelector('main h1');
      title = clean(heading && heading.textContent) || product.title;
    } else if (trigger && trigger.matches('.servicify-popup-calendar-button')) {
      const section = trigger.closest('.shopify-section, section');
      const heading = section && section.querySelector('h1, h2');
      title = clean(heading && heading.textContent);
    }
    if (!title && content) {
      const appTitle = content.querySelector('[data-servicify="event-title"]');
      title = clean(appTitle && appTitle.textContent).replace(/^(velg (en )?tid for|select a time for)\s+/i, '');
    }
    const avatar = content && content.querySelector('[data-servicify="event-avatar"]');
    const duration = shortDuration((variant && variant.title) || (product && product.duration) || '');
    const price = (variant && variant.price) || '';
    const context = { title, duration, price, image: (product && product.image) || (avatar && avatar.getAttribute('src')) || '' };
    if (title) state.context = context;
    return context;
  }

  /* Panel chrome */
  function stepTitle(content) {
    if (!isDetails(content)) return 'Velg tid';
    return content.querySelector('.Servicify-NoCheckout-Modal form') ? 'Bekreft time' : 'Timebestilling';
  }

  function ensureHeader(content) {
    let head = Array.from(content.children).find((el) => el.getAttribute(UI) === 'head');
    if (!head) {
      head = document.createElement('div');
      head.setAttribute(UI, 'head');
      head.className = 'll-booking-head';
      const title = document.createElement('h2');
      title.className = 'll-booking-head__title';
      title.id = `LaserlabBookingTitle-${state.seq += 1}`;
      head.append(title);
      content.insertBefore(head, content.firstChild);
    }
    const title = head.querySelector('.ll-booking-head__title');
    const text = stepTitle(content);
    if (title.textContent !== text) title.textContent = text;
    setAttr(content, 'aria-labelledby', title.id);
    setAttr(content, 'aria-label', text);

    // Back to the times from the details form (the provider's own close for this step).
    const canGoBack = isDetails(content) && !!content.querySelector('.Servicify-NoCheckout-Modal form') && openContents().length > 1;
    let back = head.querySelector('.ll-booking-back');
    if (canGoBack && !back) {
      back = document.createElement('button');
      back.type = 'button';
      back.className = 'll-booking-back';
      back.setAttribute('aria-label', 'Tilbake til ledige tider');
      back.append(icon('back'));
      back.addEventListener('click', () => {
        const close = directClose(content);
        if (close) close.click();
      });
      head.insertBefore(back, head.firstChild);
    } else if (!canGoBack && back) {
      back.remove();
    }
    head.classList.toggle('ll-booking-head--back', canGoBack);
  }

  function ensureClose(content) {
    const close = directClose(content);
    if (!close) return;
    if (!close.querySelector(`[${UI}="icon"]`)) close.append(icon('close'));
    setAttr(close, 'data-ll-icon', '');
    setAttr(close, 'aria-label', openContents().length > 1 && isDetails(content) ? 'Lukk timebestilling' : 'Lukk');
  }

  function bodyOf(content) {
    const last = content.lastElementChild;
    if (!last || last.tagName !== 'DIV' || last.hasAttribute(UI)) return null;
    setAttr(last, 'data-ll-booking-body', '');
    return last;
  }

  function ensureSummary(content, body) {
    const context = resolveContext(content);
    // In the details step the booked time range already states the length, so only the price remains.
    const meta = (isDetails(content) ? [context.price] : [context.duration, context.price]).filter(Boolean).join(' · ');
    const key = `${context.title}|${meta}|${context.image}`;
    let summary = Array.from(body.children).find((el) => el.getAttribute(UI) === 'summary');
    if (!context.title) {
      if (summary) summary.remove();
      return;
    }
    if (summary && summary.dataset.key === key) return;
    if (summary) summary.remove();
    summary = document.createElement('div');
    summary.setAttribute(UI, 'summary');
    summary.dataset.key = key;
    summary.className = 'll-booking-summary';
    if (context.image) {
      const image = document.createElement('img');
      image.className = 'll-booking-summary__media';
      image.src = context.image;
      image.alt = '';
      image.width = 56;
      image.height = 56;
      image.decoding = 'async';
      summary.classList.add('ll-booking-summary--media');
      summary.append(image);
    }
    const text = document.createElement('p');
    text.className = 'll-booking-summary__text';
    const name = document.createElement('span');
    name.className = 'll-booking-summary__name';
    name.textContent = context.title;
    text.append(name);
    if (meta) {
      const line = document.createElement('span');
      line.className = 'll-booking-summary__meta';
      line.textContent = meta;
      text.append(line);
    }
    summary.append(text);
    body.insertBefore(summary, body.firstChild);
  }

  /* Copy, dates and times */
  function translate(content) {
    const walker = document.createTreeWalker(content, NodeFilter.SHOW_TEXT, {
      acceptNode: (node) => (node.parentElement && node.parentElement.closest(`style, script, [${UI}]`) ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT),
    });
    const nodes = [];
    while (walker.nextNode()) nodes.push(walker.currentNode);
    nodes.forEach((node) => {
      const key = node.nodeValue.trim();
      const value = key && COPY.get(key);
      if (value) setText(node, node.nodeValue.replace(key, value));
    });
    content.querySelectorAll('[aria-label]').forEach((el) => {
      const value = ARIA.get(el.getAttribute('aria-label'));
      if (value) el.setAttribute('aria-label', value);
    });
    content.querySelectorAll('[lang^="en"]').forEach((el) => el.setAttribute('lang', 'nb'));
  }

  function enhanceCalendar(content) {
    content.querySelectorAll('.DayPicker-Weekday abbr').forEach((abbr) => {
      const node = firstText(abbr);
      const value = node && WEEKDAY_ABBR[node.nodeValue.trim().slice(0, 2).toLowerCase()];
      if (value) setText(node, value);
      const index = WEEKDAYS_EN.indexOf(abbr.getAttribute('title'));
      if (index > -1) abbr.setAttribute('title', WEEKDAYS[index]);
    });
    content.querySelectorAll('.DayPicker-Caption span').forEach((span) => {
      const node = firstText(span);
      const index = node ? MONTHS_EN.indexOf(node.nodeValue.trim()) : -1;
      if (index > -1) setText(node, MONTHS[index]);
    });
    content.querySelectorAll('.DayPicker-Day[aria-label]').forEach((day) => {
      const label = day.getAttribute('aria-label');
      if (/^[a-zæøå]+dag \d/.test(label)) return;
      const date = parseDate(label);
      if (date) day.setAttribute('aria-label', `${WEEKDAYS[date.getDay()]} ${date.getDate()}. ${MONTHS[date.getMonth()]} ${date.getFullYear()}`);
    });

    // A month button that cannot be used is not a tab stop.
    content.querySelectorAll('.DayPicker-NavButton').forEach((button) => {
      const disabled = button.classList.contains('DayPicker-NavButton--interactionDisabled');
      setAttr(button, 'tabindex', disabled ? '-1' : '0');
      if (disabled) setAttr(button, 'aria-disabled', 'true');
      else if (button.hasAttribute('aria-disabled')) button.removeAttribute('aria-disabled');
    });

    // One tab stop in the month: the chosen (or first bookable) date; arrow keys move between dates.
    content.querySelectorAll('.DayPicker-wrapper[tabindex="0"]').forEach((wrapper) => wrapper.setAttribute('tabindex', '-1'));
    const days = Array.from(content.querySelectorAll('.DayPicker-Day[role="gridcell"]'));
    const bookable = days.filter((day) => !day.classList.contains('DayPicker-Day--disabled') && !day.classList.contains('DayPicker-Day--outside'));
    const stop = bookable.find((day) => day.classList.contains('DayPicker-Day--selected')) || bookable[0];
    if (stop) days.forEach((day) => setAttr(day, 'tabindex', day === stop ? '0' : '-1'));
  }

  function enhanceTimes(content) {
    const day = content.querySelector('.servicify-calendar-day');
    const head = day && day.firstElementChild;
    const heading = head && head.firstElementChild && !head.firstElementChild.querySelector('button') ? head.firstElementChild : null;
    const node = firstText(heading);
    if (node) {
      const date = parseDate(node.nodeValue);
      if (date) {
        setText(node, longDate(date));
        setAttr(heading, 'data-ll-date', isoDate(date));
      }
      setAttr(heading, 'data-ll-date-heading', '');
    }

    // The toggle is hidden; if the stored preference did not apply, switch the app to 24 h once.
    const twelve = content.querySelector('[data-servicify="time-format-12h"][aria-pressed="true"]');
    const twentyFour = content.querySelector('[data-servicify="time-format-24h"]');
    if (twelve && twentyFour && !content.hasAttribute('data-ll-24h')) {
      content.setAttribute('data-ll-24h', '');
      window.setTimeout(() => { if (twentyFour.isConnected) twentyFour.click(); }, 0);
    }

    content.querySelectorAll('.servicify-calendar-day').forEach((pane) => {
      // The time list is the pane's last child (after the date row); the app only names it when it scrolls.
      const list = pane.children.length > 1 ? pane.lastElementChild : null;
      if (list && list.tagName === 'DIV') setAttr(list, 'data-ll-slots', '');
    });

    content.querySelectorAll('[data-ll-slots] > *').forEach((slot) => {
      const label = slot.firstElementChild && slot.firstElementChild.tagName === 'DIV' ? slot.firstElementChild : slot;
      const texts = Array.from(label.childNodes).filter((n) => n.nodeType === Node.TEXT_NODE);
      const match = texts.length ? TIME_RANGE.exec(texts.map((n) => n.nodeValue).join('')) : null;
      if (!match) return;
      const start = to24(match[1], match[2], match[3] || match[6]);
      const end = match[4] ? to24(match[4], match[5], match[6] || match[3]) : '';
      if (end) setAttr(slot, 'data-ll-range', `${start}–${end}`);
      texts.forEach((text, index) => setText(text, index === 0 ? start : ''));
      const range = slot.getAttribute('data-ll-range');
      setAttr(slot, 'data-ll-slot', '');
      setAttr(slot, 'role', 'button');
      setAttr(slot, 'tabindex', '0');
      setAttr(slot, 'aria-label', range ? `${range.replace('–', ' til ')}` : start);
    });
  }

  function enhanceDetails(content) {
    // "28 September 2026 fra 11:20 - 12:00 (Europe/Oslo) til …" -> "Mandag 28. september kl. 11:20–12:00"
    const when = content.querySelector('[data-servicify="booking-preview-selected-time"] span');
    const node = firstText(when);
    if (node) {
      const date = parseDate(node.nodeValue);
      const times = /(\d{1,2}[:.]\d{2}\s*(?:[AaPp]\.?[Mm]\.?)?)\s*[-–]\s*(\d{1,2}[:.]\d{2}\s*(?:[AaPp]\.?[Mm]\.?)?)/.exec(node.nodeValue);
      const timezone = timezoneIn(node.nodeValue);
      const zoneNote = timezone && timezone !== HOME_TIMEZONE ? ` (${timezone})` : '';
      if (date && times) setText(node, `${longDate(date)} kl.\u00a0${parseTime(times[1])}–${parseTime(times[2])}${zoneNote}`);
    }
    // Every field in this form is required: the provider's asterisks are hidden, so tell assistive tech.
    content.querySelectorAll('label[for]').forEach((label) => {
      const mark = label.querySelector('[aria-hidden="true"]');
      const field = mark && clean(mark.textContent) === '*' ? document.getElementById(label.getAttribute('for')) : null;
      if (field && content.contains(field)) setAttr(field, 'aria-required', 'true');
    });
    preferNorway(content);
    ensureNewsletter(content);
  }

  /* The phone field starts at Norway (+47). Without a default country on the event, the app takes the region of the
     browser language, so an English browser shows +1. Done once per field, through the app's own country list,
     and only while no number is typed; a country the customer picks is kept. */
  function preferNorway(content) {
    const field = content.querySelector('[data-servicify="phone-input"]');
    if (!field || field.hasAttribute('data-ll-country')) return;
    const toggle = field.querySelector('button[aria-haspopup]');
    const input = field.querySelector('input[type="tel"]');
    const flag = toggle && toggle.querySelector('[data-country]');
    if (!toggle || !input || !flag) return;
    field.setAttribute('data-ll-country', flag.dataset.country);
    if (flag.dataset.country === 'no' || input.value.replace(/\D/g, '').length > 4) return;
    const scroller = input.closest('.ReactModal__Content');
    const top = scroller ? scroller.scrollTop : 0;
    const root = document.documentElement;
    root.classList.add('ll-phone-country-switch');
    toggle.click();
    let frames = 0;
    const pick = () => {
      const menu = document.querySelector('[data-servicify="phone-country-menu"]');
      const norway = menu && Array.from(menu.querySelectorAll('[role="option"]')).find((option) => option.querySelector('[data-country="no"]'));
      if (!norway && frames++ < 20) { window.requestAnimationFrame(pick); return; }
      if (norway) norway.click();
      else if (menu) toggle.click();
      if (document.activeElement === input || document.activeElement === toggle) document.activeElement.blur();
      if (scroller) scroller.scrollTop = top;
      root.classList.remove('ll-phone-country-switch');
      const now = toggle.querySelector('[data-country]');
      if (now) field.setAttribute('data-ll-country', now.dataset.country);
    };
    window.requestAnimationFrame(pick);
  }

  /* Newsletter beside the booking terms: unticked, one line and a link to the privacy policy. On «Bekreft reservasjon»
     with everything filled in, the address goes through the shop's own sign-up form (hidden, snippets/laserlab-booking-data.liquid),
     the same list as the footer. */
  function ensureNewsletter(content) {
    const signup = document.getElementById('LaserlabBookingNewsletter');
    if (!signup || content.querySelector(`[${UI}="newsletter"]`)) return;
    const wrappers = content.querySelectorAll('[data-servicify="custom-field-wrapper"]');
    const last = wrappers[wrappers.length - 1];
    const form = content.querySelector('.Servicify-NoCheckout-Modal');
    if (!last && !form) return;
    const row = document.createElement('div');
    row.setAttribute(UI, 'newsletter');
    row.className = 'll-booking-newsletter';
    const box = document.createElement('input');
    box.type = 'checkbox';
    box.id = 'LaserlabBookingNewsletterConsent';
    box.checked = state.newsletter === true;
    box.addEventListener('change', () => { state.newsletter = box.checked; });
    const text = document.createElement('div');
    const label = document.createElement('label');
    label.htmlFor = box.id;
    label.textContent = signup.dataset.label || 'Ja takk, send meg nyhetsbrev fra Laserlab.';
    const note = document.createElement('p');
    note.className = 'll-booking-newsletter__note';
    note.append(`${signup.dataset.note || 'Avmelding når som helst.'} `);
    if (signup.dataset.privacy) {
      const privacy = document.createElement('a');
      privacy.href = signup.dataset.privacy;
      privacy.target = '_blank';
      privacy.rel = 'noopener';
      privacy.textContent = 'Personvern';
      note.append(privacy, '.');
    }
    text.append(label, note);
    row.append(box, text);
    if (last) last.after(row);
    else form.before(row);
  }

  const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  function subscribeFromBooking(content) {
    if (state.newsletter !== true) return;
    const signup = document.getElementById('LaserlabBookingNewsletter');
    const email = content.querySelector('input[type="email"]');
    const address = email ? email.value.trim() : '';
    if (!signup || !EMAIL.test(address) || state.subscribed === address.toLowerCase()) return;
    // Only when the booking can go through: every required field filled and every term ticked.
    const missing = Array.from(content.querySelectorAll('input[aria-required="true"], input[required]'))
      .some((field) => (field.type === 'checkbox' ? !field.checked : !field.value.trim() || /^\+\d{1,4}$/.test(field.value.trim())));
    if (missing) return;
    state.subscribed = address.toLowerCase();
    state.codePending = signup.dataset.code || '';
    // After a confirmed booking the app opens the thank-you page, which shows the welcome code (sections/laserlab-thanks.liquid).
    try { sessionStorage.setItem('ll-welcome-code', '1'); } catch (error) { /* storage blocked */ }
    signup.querySelector('input[name="contact[email]"]').value = address;
    if (typeof signup.requestSubmit === 'function') signup.requestSubmit();
    else signup.submit();
  }

  /* After a confirmed booking (the app's confirmation shows its check mark) the new subscriber gets the welcome code
     in the panel, with a link that applies it in the shop. Nothing shows if the booking was not confirmed. */
  function showCode(content) {
    const code = state.codePending;
    if (!code || !content.querySelector('.checkmark-circle') || content.querySelector(`[${UI}="welcome-code"]`)) return;
    const signup = document.getElementById('LaserlabBookingNewsletter');
    const box = document.createElement('div');
    box.setAttribute(UI, 'welcome-code');
    box.className = 'll-booking-code';
    const intro = document.createElement('p');
    intro.className = 'll-booking-code__intro';
    intro.textContent = 'Takk for at du abonnerer. Her er koden din for 15 % på første kjøp i nettbutikken:';
    const value = document.createElement('p');
    value.className = 'll-booking-code__value';
    value.textContent = code;
    const shop = document.createElement('a');
    shop.className = 'll-booking-code__shop';
    shop.href = `/discount/${encodeURIComponent(code)}?redirect=${encodeURIComponent((signup && signup.dataset.shop) || '/collections/all')}`;
    shop.textContent = 'Handle i nettbutikken';
    const note = document.createElement('p');
    note.className = 'll-booking-code__note';
    note.textContent = 'Koden legges til automatisk.';
    box.append(intro, value, shop, note);
    const mark = content.querySelector('.checkmark-circle');
    const host = (mark && mark.closest('svg') && mark.closest('svg').parentElement && mark.closest('svg').parentElement.parentElement) || bodyOf(content) || content;
    host.append(box);
    state.codePending = '';
  }

  function markFilterRow(content) {
    const row = content.querySelector('.servicify-filter-row');
    if (!row) return;
    const visible = Array.from(row.querySelectorAll('[data-servicify="employee-selector"] > *')).some((el) => el.getClientRects().length > 0 && el.offsetHeight > 0);
    if (row.hasAttribute('data-ll-empty') === visible) row.toggleAttribute('data-ll-empty', !visible);
  }

  const appGaveUp = () => appLoad.lookup > 0 && appLoad.view > 0
    && performance.now() - Math.max(appLoad.lookup, appLoad.view, state.requestedAt) > 1000;

  function ensureFallback(content, body) {
    const loaded = content.querySelector('.DayPicker, .servicify-skeleton, [data-servicify="calendar-loading"], form');
    let note = body && Array.from(body.children).find((el) => el.getAttribute(UI) === 'fallback');
    if (loaded || !body || (!appGaveUp() && Date.now() - state.openedAt < 8000)) {
      if (note && loaded) note.remove();
      return;
    }
    if (note) return;
    note = document.createElement('p');
    note.setAttribute(UI, 'fallback');
    note.setAttribute('role', 'status');
    note.className = 'll-booking-fallback';
    const contact = document.createElement('a');
    contact.href = `${(window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/'}pages/contact`;
    contact.textContent = 'kontakt oss';
    // The clinic's phone number as the page shows it (footer), so booking by phone is one tap away.
    const phone = Array.from(document.querySelectorAll('a[href^="tel:"]')).find((el) => !el.closest(PORTAL) && clean(el.textContent));
    if (phone) {
      const call = document.createElement('a');
      call.href = phone.getAttribute('href');
      call.textContent = clean(phone.textContent);
      note.append('Ledige tider kunne ikke lastes. Prøv igjen, ring ', call, ' eller ', contact, '.');
    } else {
      note.append('Ledige tider kunne ikke lastes. Prøv igjen, eller ', contact, '.');
    }
    body.append(note);
  }

  function watchAppLoad() {
    if (!('PerformanceObserver' in window)) return;
    try {
      new PerformanceObserver((list) => {
        list.getEntries().forEach((entry) => {
          if (/servicify[^/]*\/api\/products\/\d+\/events/i.test(entry.name)) appLoad.lookup = entry.responseEnd;
          else if (/CalendarView[^/]*\.js/i.test(entry.name)) appLoad.view = entry.responseEnd;
          else return;
          if (state.base) window.setTimeout(queue, 1100);
        });
      }).observe({ type: 'resource', buffered: true });
    } catch (error) { /* not supported: the timed fallback applies */ }
  }

  function markTimezone(content) {
    const zone = content.querySelector('[data-servicify="event-timezone"]');
    const timezone = zone ? timezoneIn(zone.textContent) : '';
    const foreign = !!timezone && timezone !== HOME_TIMEZONE;
    if (content.hasAttribute('data-ll-timezone') !== foreign) content.toggleAttribute('data-ll-timezone', foreign);
  }

  function enhance(content, layer) {
    const overlay = content.parentElement;
    if (overlay && overlay.matches('.ReactModal__Overlay')) setAttr(overlay, 'data-ll-booking-layer', String(layer));
    const details = isDetails(content);
    setAttr(content, 'data-ll-booking', details ? 'details' : 'calendar');
    ensureHeader(content);
    ensureClose(content);
    const body = bodyOf(content);
    if (body) ensureSummary(content, body);
    translate(content);
    if (details) {
      enhanceDetails(content);
    } else {
      enhanceCalendar(content);
      enhanceTimes(content);
      markTimezone(content);
      markFilterRow(content);
      ensureFallback(content, body);
    }
    if (content === state.base) content.toggleAttribute('data-ll-settling', state.hidden);
    showCode(content);
    focusClose(content);
  }

  /* First available date: on open, and when a month without the selected date is shown. */
  function scheduleAutoSelect(delay) {
    window.clearTimeout(state.autoTimer);
    state.autoTimer = window.setTimeout(autoSelect, delay || 200);
  }

  // Times stay hidden on open until a bookable date is chosen, so an empty "today" never flashes.
  function reveal() {
    if (!state.hidden) return;
    state.hidden = false;
    if (state.base) state.base.removeAttribute('data-ll-settling');
  }

  function settle() {
    reveal();
    if (!state.initial) return;
    state.initial = false;
    window.clearTimeout(state.settleTimer);
  }

  function autoSelect() {
    const base = state.base;
    if (!base || !base.isConnected || openContents().length > 1 || isDetails(base)) return;
    const elapsed = Date.now() - state.openedAt;
    // Month availability still loading.
    if (base.querySelector('[data-servicify="calendar-loading"]')) {
      scheduleAutoSelect(250);
      return;
    }
    const days = Array.from(base.querySelectorAll('.DayPicker-Day')).filter((day) => !day.classList.contains('DayPicker-Day--outside'));
    if (!days.length) {
      if (elapsed < 15000) scheduleAutoSelect(300);
      else settle();
      return;
    }
    const enabled = days.filter((day) => !day.classList.contains('DayPicker-Day--disabled'));
    // Before availability arrives the provider briefly renders every day as bookable, past days included.
    // Only when today is in the month shown: in the next month, today can appear as a greyed day from the previous month.
    const todayIndex = days.findIndex((day) => day.classList.contains('DayPicker-Day--today'));
    if (todayIndex > 0 && days.slice(0, todayIndex).some((day) => enabled.includes(day))) {
      if (elapsed < 10000) scheduleAutoSelect(250);
      else settle();
      return;
    }
    const selected = enabled.find((day) => day.classList.contains('DayPicker-Day--selected'));
    if (selected) {
      reveal();
      // On open, wait for the chosen day's times, and skip a bookable-looking day that has none left.
      if (state.initial && base.querySelector('[data-servicify="slots-skeleton"], [data-ll-slots] .servicify-skeleton') && elapsed < 8000) {
        scheduleAutoSelect(250);
        return;
      }
      const slots = base.querySelector('[data-ll-slots] > [data-ll-slot]');
      const empty = !slots && !!base.querySelector('[data-ll-slots] > [data-servicify="no-timeslots"]');
      const next = enabled[enabled.indexOf(selected) + 1];
      // A busy clinic can be fully booked for weeks: keep looking for the first day with free times (up to 12 days),
      // into the next month when this one runs out.
      if (state.initial && empty && state.emptyDaySkips < 12) {
        if (next) {
          state.emptyDaySkips += 1;
          next.click();
          scheduleAutoSelect(300);
          return;
        }
        const nextMonthButton = base.querySelector('.DayPicker-NavButton--next:not(.DayPicker-NavButton--interactionDisabled)');
        if (nextMonthButton && state.monthSkips < 2) {
          state.monthSkips += 1;
          state.emptyDaySkips += 1;
          nextMonthButton.click();
          scheduleAutoSelect(400);
          return;
        }
      }
      settle();
      return;
    }
    const month = clean((base.querySelector('.DayPicker-Caption') || {}).textContent);
    if (enabled.length && (state.picks[month] || 0) < 2) {
      state.picks[month] = (state.picks[month] || 0) + 1;
      enabled[0].click();
      scheduleAutoSelect(300);
      return;
    }
    const nextMonth = base.querySelector('.DayPicker-NavButton--next:not(.DayPicker-NavButton--interactionDisabled)');
    if (state.initial && !enabled.length && nextMonth && state.monthSkips < 2) {
      state.monthSkips += 1;
      nextMonth.click();
      scheduleAutoSelect(400);
      return;
    }
    settle();
  }

  /* Session: scroll lock, focus and closing */
  function lockScroll() {
    const root = document.documentElement;
    if (root.classList.contains('ll-booking-open')) return;
    const gap = window.innerWidth - root.clientWidth;
    root.style.setProperty('--ll-booking-scrollbar', `${gap > 0 ? gap : 0}px`);
    root.classList.add('ll-booking-open');
  }

  function unlockScroll() {
    const root = document.documentElement;
    root.classList.remove('ll-booking-open');
    root.style.removeProperty('--ll-booking-scrollbar');
  }

  function focusClose(content) {
    if (state.focused.has(content)) return;
    state.focused.add(content);
    window.setTimeout(() => {
      const close = directClose(content);
      const active = document.activeElement;
      if (!close || !content.isConnected) return;
      if (!active || active === document.body || active === content || !content.contains(active)) close.focus({ preventScroll: true });
    }, 60);
  }

  function onOpen(base) {
    state.base = base;
    state.openedAt = Date.now();
    state.initial = true;
    state.hidden = true;
    state.monthSkips = 0;
    state.emptyDaySkips = 0;
    state.picks = {};
    lockScroll();
    const accent = state.trigger ? window.getComputedStyle(state.trigger).getPropertyValue('--ll-button-hover-background').trim() : '';
    document.querySelectorAll(PORTAL).forEach((portal) => {
      if (accent) portal.style.setProperty('--ll-button-hover-background', accent);
      else portal.style.removeProperty('--ll-button-hover-background');
    });
    window.clearTimeout(state.settleTimer);
    state.settleTimer = window.setTimeout(() => { settle(); queue(); }, 4000);
    window.clearTimeout(state.fallbackTimer);
    state.fallbackTimer = window.setTimeout(queue, 8100);
    window.setTimeout(queue, 1200);
  }

  function onClose() {
    state.base = null;
    state.initial = false;
    state.hidden = false;
    state.context = null;
    window.clearTimeout(state.autoTimer);
    window.clearTimeout(state.settleTimer);
    window.clearTimeout(state.fallbackTimer);
    unlockScroll();
    const trigger = state.trigger;
    window.setTimeout(() => {
      const active = document.activeElement;
      if (openContents().length || !trigger || !trigger.isConnected) return;
      if (!active || active === document.body || active === document.documentElement) trigger.focus({ preventScroll: true });
    }, 0);
  }

  function closeAll() {
    const base = baseOf(openContents());
    if (!base) return;
    const close = directClose(base) || base.querySelector(CLOSE);
    if (close) close.click();
    ensureClosed(base);
  }

  // A booking app that has crashed (for example on an event that no longer exists) no longer reacts
  // to its own close control. If its dialog is still open a moment after a close request, this layer
  // hides that portal (nodes stay in place) so the visitor is never stuck behind a locked page.
  // The next press on a booking button shows it again.
  function ensureClosed(content) {
    window.setTimeout(() => {
      const portal = content.isConnected ? content.closest(PORTAL) : null;
      if (!portal || !openContents().includes(content)) return;
      const active = document.activeElement;
      portal.setAttribute(DISMISSED, '');
      if (state.trigger && state.trigger.isConnected && (!active || active === document.body || portal.contains(active))) {
        state.trigger.focus({ preventScroll: true });
      }
      queue();
    }, 250);
  }

  /* Observation: one observer per provider portal, paused while this layer writes. */
  const watched = new Map();
  let queued = false;

  function pause() { watched.forEach((observer) => observer.disconnect()); }
  // A closed dialog's portal is empty: then only its opening (a child being added) is watched.
  function resume() {
    watched.forEach((observer, portal) => {
      if (portal.isConnected) observer.observe(portal, portal.firstElementChild ? OBSERVE : OBSERVE_IDLE);
      else watched.delete(portal);
    });
  }

  function run() {
    const contents = openContents();
    const base = baseOf(contents);
    if (base !== state.base) {
      if (state.base) onClose();
      if (base) onOpen(base);
    }
    if (!contents.length) {
      pause();
      resume();
      return;
    }
    // The app hides <body> from assistive tech while open, which also hides its own dialog (it lives in body).
    // The dialog is aria-modal, so the page behind stays out of reach without it.
    if (document.body.getAttribute('aria-hidden') === 'true') document.body.removeAttribute('aria-hidden');
    pause();
    try {
      contents.forEach((content) => enhance(content, content === base ? 1 : 2));
    } finally {
      resume();
    }
    scheduleAutoSelect();
  }

  function queue() {
    if (queued) return;
    queued = true;
    Promise.resolve().then(() => {
      queued = false;
      run();
    });
  }

  function watch(portal) {
    if (watched.has(portal)) return;
    const observer = new MutationObserver(queue);
    watched.set(portal, observer);
    observer.observe(portal, portal.firstElementChild ? OBSERVE : OBSERVE_IDLE);
    queue();
  }

  function onClick(event) {
    const confirmButton = isEl(event.target) ? event.target.closest('[data-ll-booking="details"] .Servicify-NoCheckout-Modal button[data-servicify]') : null;
    if (confirmButton) subscribeFromBooking(confirmButton.closest('[data-ll-booking="details"]'));
    const target = isEl(event.target) ? event.target : null;
    if (!target) return;
    const trigger = target.closest(TRIGGER);
    if (trigger && !trigger.closest(PORTAL)) {
      state.trigger = trigger;
      state.context = null;
      state.requestedAt = performance.now();
      prefer24h();
      const dismissed = document.querySelectorAll(`${PORTAL}[${DISMISSED}]`);
      if (dismissed.length) {
        dismissed.forEach((portal) => portal.removeAttribute(DISMISSED));
        queue();
      }
      return;
    }
    const slot = target.closest('[data-ll-slot]');
    if (slot && slot.parentElement) {
      Array.from(slot.parentElement.children).forEach((el) => el.toggleAttribute('data-ll-selected', el === slot));
    }
    if (!event.isTrusted) return;
    const contents = openContents();
    // One dialog: the app closes it on its own close control or the backdrop; make sure it did.
    if (contents.length === 1 && (target.closest(CLOSE) || (!target.closest(CONTENT) && target.closest(PORTAL)))) ensureClosed(contents[0]);
    if (contents.length < 2) return;
    // With the details step open, its close control and the backdrop end the whole booking.
    const base = baseOf(contents);
    const top = contents.find((el) => el !== base);
    const close = target.closest(CLOSE);
    const outside = !target.closest(CONTENT) && !!target.closest(PORTAL);
    if (outside || (close && top && close.parentElement === top)) {
      event.preventDefault();
      event.stopPropagation();
      closeAll();
    }
  }

  function onKeydown(event) {
    if (!state.base) return;
    const target = isEl(event.target) ? event.target : null;
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      // An open list inside the step (country code) closes first; the booking stays open.
      const popup = document.querySelector(`${PORTAL} [aria-haspopup][aria-expanded="true"]`);
      if (popup) {
        popup.click();
        popup.focus({ preventScroll: true });
        return;
      }
      closeAll();
      return;
    }
    if ((event.key === 'Enter' || event.key === ' ') && target && target.matches('[data-ll-slot]')) {
      event.preventDefault();
      target.click();
    }
  }

  // The country list for the phone field is rendered outside the dialog, directly in <body>.
  function watchMenu(menu) {
    const translateMenu = () => {
      observer.disconnect();
      translate(menu);
      if (menu.getAttribute('aria-label') === 'Country') menu.setAttribute('aria-label', 'Land');
      if (menu.isConnected) observer.observe(menu, { childList: true, subtree: true, characterData: true });
    };
    const observer = new MutationObserver(translateMenu);
    translateMenu();
  }

  /* Keep the product submit markup Servicify uses to mount its button, but never submit
     a treatment to the cart while waiting for that button. Late app loads recover the
     same queued intent; a timeout offers another attempt and the studio's phone number. */
  function initPlaceholders() {
    const entries = [];
    let sequence = 0;
    const discover = () => Array.from(document.querySelectorAll('[data-ll-booking-placeholder]')).forEach((button) => {
      if (entries.some((entry) => entry.button === button)) return;
      const scope = button.closest('.product-form') || button.form || button.parentElement;
      const product = scope.closest('[data-product-id]');
      const variant = button.form && button.form.querySelector('[name="id"]');
      const productId = product ? product.getAttribute('data-product-id') : variant ? variant.value : '';
      const key = `${button.id}|${productId}`;
      const obsolete = entries.find((entry) => !entry.button.isConnected && entry.button.id === button.id);
      const previous = obsolete && obsolete.key === key ? obsolete : null;
      const status = document.createElement('p');
      status.id = `${button.id || 'laserlab-booking'}-status-${sequence++}`;
      status.setAttribute('role', 'status');
      status.setAttribute('aria-live', 'polite');
      status.style.cssText = 'margin:8px 0 0;font-size:14px;line-height:1.5;font-weight:400;';
      status.hidden = true;
      const message = document.createElement('span');
      const contact = document.createElement('a');
      contact.href = 'tel:+4745801000';
      contact.textContent = '458 01 000';
      contact.hidden = true;
      status.append(message, contact);
      button.insertAdjacentElement('afterend', status);
      const describedBy = (button.getAttribute('aria-describedby') || '').split(/\s+/).filter((id) => id && (!obsolete || id !== obsolete.status.id));
      button.setAttribute('aria-describedby', [...describedBy, status.id].join(' '));
      if (previous) {
        if (previous.button.hasAttribute('data-ll-queued')) button.setAttribute('data-ll-queued', '');
      }
      if (obsolete) {
        obsolete.status.remove();
        entries.splice(entries.indexOf(obsolete), 1);
      }
      entries.push({ button, scope, key, status, message, contact, started: previous ? previous.started : Date.now() });
    });
    let timer = 0;
    let checking = false;
    const appButton = (entry) => Array.from(entry.scope.querySelectorAll(TRIGGER)).find((el) =>
      !el.hasAttribute('data-ll-booking-placeholder') && !el.disabled && el.offsetParent !== null
    );
    const notice = (entry, message, showContact = false) => {
      if (entry.message.textContent !== message) entry.message.textContent = message;
      if (entry.contact.hidden === showContact) entry.contact.hidden = !showContact;
      if (entry.status.hidden === !!message) entry.status.hidden = !message;
    };
    const ready = (entry) => {
      entry.button.removeAttribute('aria-busy');
      notice(entry, '');
    };
    const tick = () => {
      discover();
      window.clearTimeout(timer);
      timer = 0;
      let waiting = false;
      const open = new Set();
      entries.forEach((entry) => {
        const { button } = entry;
        if (!button.isConnected || button.disabled) return;
        const app = appButton(entry);
        if (app) {
          ready(entry);
          if (button.hasAttribute('data-ll-queued')) {
            button.removeAttribute('data-ll-queued');
            open.add(app);
          }
        } else if (Date.now() - entry.started > 20000) {
          button.removeAttribute('aria-busy');
          notice(entry, 'Kalenderen bruker lengre tid enn vanlig. Prøv igjen eller ring ', true);
        } else {
          waiting = true;
        }
      });
      open.forEach((app) => app.click());
      if (waiting) timer = window.setTimeout(tick, 250);
    };
    const request = (entry) => {
      if (entry.button.disabled) return;
      const app = appButton(entry);
      if (app) {
        entry.button.removeAttribute('data-ll-queued');
        ready(entry);
        app.click();
      } else {
        entry.started = Date.now();
        entry.button.setAttribute('aria-busy', 'true');
        entry.button.setAttribute('data-ll-queued', '');
        notice(entry, 'Laster ledige tider …');
        tick();
      }
    };
    document.addEventListener('click', (event) => {
      const button = isEl(event.target) ? event.target.closest('[data-ll-booking-placeholder]') : null;
      if (!button) return;
      discover();
      const entry = entries.find((item) => item.button === button);
      if (!entry) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      request(entry);
    }, true);
    document.addEventListener('submit', (event) => {
      discover();
      const entry = entries.find((item) => item.button.form === event.target && item.button.hasAttribute('data-ll-booking-placeholder'));
      if (!entry) return;
      // The theme's submit handler does not check defaultPrevented.
      event.preventDefault();
      event.stopImmediatePropagation();
      request(entry);
    }, true);
    new MutationObserver(() => {
      if (checking) return;
      checking = true;
      Promise.resolve().then(() => { checking = false; tick(); });
    }).observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'style', 'hidden'] });
    tick();
  }

  // Servicify emits this after its no-checkout booking request succeeds.
  // Carry only an opaque confirmation to the thanks page; never copy its payload.
  function initCompletionTracking() {
    const seen = new WeakSet();
    const storageKey = 'll-booking-confirmation-v1';
    const shopify = window.Shopify;
    if (shopify && !shopify.customerPrivacy && typeof shopify.loadFeatures === 'function') {
      try {
        shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], () => {});
      } catch (error) { /* Missing privacy API: no conversion tracking. */ }
    }
    function allowed(method) {
      try {
        const privacy = window.Shopify && window.Shopify.customerPrivacy;
        return !!privacy && typeof privacy[method] === 'function' && privacy[method]() === true;
      } catch (error) { return false; }
    }
    document.addEventListener('Servicify.AddedToCart', (event) => {
      const detail = event.detail;
      if (!detail || detail.type !== 'no-checkout' || !detail.payload || typeof detail.payload !== 'object') return;
      if (seen.has(detail.payload)) return;
      seen.add(detail.payload);
      const eventId = window.crypto && typeof window.crypto.randomUUID === 'function'
        ? window.crypto.randomUUID()
        : `ll-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const analyticsAllowed = allowed('analyticsProcessingAllowed');
      const marketingAllowed = allowed('marketingAllowed');
      try {
        if (analyticsAllowed || marketingAllowed) {
          sessionStorage.setItem(storageKey, JSON.stringify({
            id: eventId, createdAt: Date.now(), analyticsAllowed, marketingAllowed,
            googleSent: false, metaSent: false,
          }));
        } else {
          sessionStorage.removeItem(storageKey);
        }
      } catch (error) { /* Storage blocked: booking still completes normally. */ }
      try {
        const analytics = window.Shopify && window.Shopify.analytics;
        if (analytics && typeof analytics.publish === 'function') {
          const result = analytics.publish('laserlab:booking_confirmed', { event_id: eventId });
          if (result && typeof result.catch === 'function') result.catch(() => {});
        }
      } catch (error) { /* Analytics must never interrupt a successful booking. */ }
    });
  }

  function init() {
    initPlaceholders();
    initCompletionTracking();
    prefer24h();
    watchAppLoad();
    document.querySelectorAll(PORTAL).forEach(watch);
    new MutationObserver((mutations) => {
      mutations.forEach((mutation) => mutation.addedNodes.forEach((node) => {
        if (!isEl(node)) return;
        if (node.matches(PORTAL)) watch(node);
        else if (node.matches('[data-servicify="phone-country-menu"]')) watchMenu(node);
      }));
    }).observe(document.body, { childList: true });
    document.addEventListener('click', onClick, true);
    document.addEventListener('keydown', onKeydown, true);
  }

  window.LaserlabBooking = { close: closeAll };
  if (document.body) init();
  else document.addEventListener('DOMContentLoaded', init, { once: true });
})();
